//
//  SDKTextToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2021 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { TextCase } from "../enums/SDKTextCase";
import { TokenType } from "../enums/SDKTokenType";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class TextCaseToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        this.tokenType = TokenType.textCase;
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    static create(versionId, brandId, name, description, value, alias, properties, propertyValues) {
        var _a, _b;
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type: TokenType.textCase,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            const tokenValue = this.textCaseFromDefinition(value);
            return new this(versionId, baseToken, tokenValue, 
            // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
            undefined, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            const tokenValue = {
                value: (_a = alias.value) === null || _a === void 0 ? void 0 : _a.value,
                options: (_b = alias.value) === null || _b === void 0 ? void 0 : _b.options,
                referencedTokenId: alias.id,
            };
            return new this(versionId, baseToken, tokenValue, 
            // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
            undefined, properties, propertyValues);
        }
        throw SupernovaError.fromMessage("Text Case Token must be created using value or alias, but none was provided");
    }
    static textCaseFromDefinition(textCase) {
        let textCaseValue = TextCase.original;
        if (textCase && typeof textCase === "string") {
            const caseToCheck = textCase.toLowerCase().trim();
            if (caseToCheck === "camel" ||
                caseToCheck === "camelcase" ||
                caseToCheck === "capitalize") {
                textCaseValue = TextCase.camel;
            }
            else if (caseToCheck === "lower" || caseToCheck === "lowercase") {
                textCaseValue = TextCase.lower;
            }
            else if (caseToCheck === "upper" || caseToCheck === "uppercase") {
                textCaseValue = TextCase.upper;
            }
            else if (caseToCheck === "smallcaps" ||
                caseToCheck === "small_caps" ||
                caseToCheck === "small_caps_forced") {
                textCaseValue = TextCase.smallCaps;
            }
            else {
                textCaseValue = TextCase.original;
            }
        }
        else {
            textCaseValue = TextCase.original;
        }
        return {
            value: textCaseValue,
            referencedTokenId: null,
        };
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Write
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = TextCaseToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a;
        const valueObject = !value.referencedTokenId ? value.value : undefined;
        return {
            aliasTo: (_a = value.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            value: valueObject,
        };
    }
}
